import { useEffect, useId, useRef, useState } from 'react';
import type { DragEvent, ReactNode, RefObject } from 'react';
import ArrowUpOutlined from '@ant-design/icons/ArrowUpOutlined';
import FolderAddOutlined from '@ant-design/icons/FolderAddOutlined';
import InfoCircleOutlined from '@ant-design/icons/InfoCircleOutlined';
import LoadingOutlined from '@ant-design/icons/LoadingOutlined';
import PaperClipOutlined from '@ant-design/icons/PaperClipOutlined';
import UploadOutlined from '@ant-design/icons/UploadOutlined';
import { useTranslation } from 'react-i18next';
import { useLlmConfig } from '@/api/llmConfig';
import type { RunMode } from '@/api/runs';
import { BLOCK_MESSAGE_KEYS, type Block, type BlockAction } from './blocking';
import ModePicker from './ModePicker';
import {
  COMPOSITION_GRACE_MS,
  isApplePlatform,
  isSendKey,
  sendHelp,
  type SendKey,
} from './sendKeys';
import { useFieldHeight } from './useFieldHeight';
import styles from './composer.module.css';

export interface ComposerProps {
  inquiryId: string | undefined;
  value: string;
  onChange: (value: string) => void;
  /** `readyOnly`: send with the ready documents, leaving pending ones out. */
  onSend: (options: { readyOnly: boolean }) => void;
  mode: RunMode;
  onModeChange: (mode: RunMode) => void;
  sendKey: SendKey;
  /** Below 640 px. */
  compact: boolean;
  /** The question is on its way to the server. */
  sending: boolean;
  /** A run is active in this Q&A: Stop instead of Send. */
  generating: boolean;
  /** Stop can be pressed (the run is known, no stop on its way). */
  canStop: boolean;
  /** Stop was pressed and the run has not ended yet. */
  stopRequested: boolean;
  onStop: () => void;
  /** A clarification waits for the user's reply: the next message answers it. */
  replying?: boolean;
  /** Why Send is disabled, with its resolutions. */
  block: Block | null;
  onBlockAction: (action: BlockAction) => void;
  onOpenSources: () => void;
  onFiles: (files: FileList | File[]) => void;
  /** The file input, so a reattachment can open it. */
  fileInput: RefObject<HTMLInputElement>;
  /** Chips, attachment rows and the comparison setup. */
  chips?: ReactNode;
  attachments?: ReactNode;
  setup?: ReactNode;
  /** For the field's height and the keyboard inset. */
  transcript: RefObject<HTMLElement> | null;
  host: RefObject<HTMLElement> | null;
}

const hasFiles = (event: Pick<DragEvent, 'dataTransfer'>) =>
  !!event.dataTransfer &&
  Array.from(event.dataTransfer.types ?? []).includes('Files');

/** Page-wide drag of files: the composer shows where to drop them. */
const usePageDrag = () => {
  const [dragging, setDragging] = useState(false);
  useEffect(() => {
    let depth = 0;
    const isFileDrag = (event: globalThis.DragEvent) =>
      !!event.dataTransfer &&
      Array.from(event.dataTransfer.types ?? []).includes('Files');
    const enter = (event: globalThis.DragEvent) => {
      if (!isFileDrag(event)) return;
      depth += 1;
      setDragging(true);
    };
    const leave = (event: globalThis.DragEvent) => {
      if (!isFileDrag(event)) return;
      depth = Math.max(0, depth - 1);
      if (depth === 0) setDragging(false);
    };
    // A file dropped beside the composer must not open in the tab.
    const over = (event: globalThis.DragEvent) => {
      if (isFileDrag(event)) event.preventDefault();
    };
    const drop = (event: globalThis.DragEvent) => {
      depth = 0;
      setDragging(false);
      if (isFileDrag(event)) event.preventDefault();
    };
    window.addEventListener('dragenter', enter);
    window.addEventListener('dragleave', leave);
    window.addEventListener('dragover', over);
    window.addEventListener('drop', drop);
    return () => {
      window.removeEventListener('dragenter', enter);
      window.removeEventListener('dragleave', leave);
      window.removeEventListener('dragover', over);
      window.removeEventListener('drop', drop);
    };
  }, []);
  return dragging;
};

const BlockingNotice = ({
  block,
  id,
  onAction,
}: {
  block: Block;
  id: string;
  onAction: (action: BlockAction) => void;
}) => {
  const { t } = useTranslation();
  const labels: Record<BlockAction, string> = {
    stop: t('v2.common.stop'),
    select_sources: t('v2.composer.selectCtx'),
    switch_chat: t('v2.composer.switchToChat'),
    send_ready_only: t('v2.composer.sendReadyOnly'),
    show_breakdown: t('v2.composer.showBreakdown'),
    choose_role:
      block.code === 'baseline_missing'
        ? t('v2.composer.chooseBaseline')
        : t('v2.composer.chooseDocToCheck'),
    retry_load: t('v2.common.retry'),
  };
  // Stop has its own button in the composer.
  const actions = block.actions.filter(action => action !== 'stop');
  return (
    <div
      id={id}
      className={styles.reason}
      role="status"
      data-testid="composer-block"
      data-code={block.code}
    >
      {block.code === 'calculating' ? (
        <LoadingOutlined aria-hidden />
      ) : (
        <InfoCircleOutlined aria-hidden />
      )}
      <span className={styles.reasonText}>
        {t(BLOCK_MESSAGE_KEYS[block.code], block.values)}
        {block.pending > 0 &&
        block.code !== 'sources_pending' &&
        block.code !== 'uploading'
          ? ` ${t('v2.composer.rPendingAlso', { count: block.pending })}`
          : null}
      </span>
      {actions.map(action => (
        <button
          key={action}
          type="button"
          className={styles.textButton}
          onClick={() => onAction(action)}
          data-testid={`composer-block-${action}`}
        >
          {labels[action]}
        </button>
      ))}
    </div>
  );
};

/**
 * The composer (spec §3): sources, attachments, mode and Send or Stop. The
 * field grows with its text (useFieldHeight), the Send key follows the
 * preference, and every reason Send is disabled is explained with its
 * resolution.
 */
const Composer = ({
  inquiryId,
  value,
  onChange,
  onSend,
  mode,
  onModeChange,
  sendKey,
  compact,
  sending,
  generating,
  canStop,
  stopRequested,
  onStop,
  replying = false,
  block,
  onBlockAction,
  onOpenSources,
  onFiles,
  fileInput,
  chips,
  attachments,
  setup,
  transcript,
  host,
}: ComposerProps) => {
  const { t } = useTranslation();
  const config = useLlmConfig();
  const field = useRef<HTMLTextAreaElement>(null);
  const composedAt = useRef(0);
  const [overComposer, setOverComposer] = useState(0);
  const pageDrag = usePageDrag();
  const reasonId = useId();
  const helpId = useId();
  useFieldHeight({ field, transcript, host, value });

  const hasInquiry = !!inquiryId;
  // A run's Stop needs no explanation; every other reason is shown.
  const shownBlock = block && block.code !== 'run_active' ? block : null;
  const blank = value.trim() === '';
  const canSend = hasInquiry && !sending && !generating && !blank && !block;
  const help = sendHelp(sendKey, isApplePlatform());
  const accept = config.uploads.accepted_extensions
    .map(extension => `.${extension}`)
    .join(',');

  const drop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setOverComposer(0);
    const files = event.dataTransfer?.files;
    if (files && files.length) onFiles(files);
  };

  return (
    <div className={styles.wrap}>
      <div
        className={styles.composer}
        data-drag={overComposer ? 'over' : pageDrag ? 'page' : undefined}
        onDragEnter={event => {
          if (!hasFiles(event)) return;
          event.preventDefault();
          setOverComposer(depth => depth + 1);
        }}
        onDragLeave={event => {
          if (!hasFiles(event)) return;
          setOverComposer(depth => Math.max(0, depth - 1));
        }}
        onDragOver={event => {
          if (hasFiles(event)) event.preventDefault();
        }}
        onDrop={drop}
        data-testid="composer"
      >
        {overComposer || pageDrag ? (
          <div className={styles.dropHint} aria-hidden="true">
            <UploadOutlined />
            {overComposer
              ? t('v2.composer.dropHere')
              : t('v2.composer.dropTarget')}
          </div>
        ) : null}
        {/* Many sources or attachments scroll here, so the field and Send
            stay on screen in a short viewport. */}
        <div className={styles.extras}>
          {chips}
          {attachments}
        </div>
        <label htmlFor={`${reasonId}-field`} className={styles.srOnly}>
          {t('v2.composer.composerLabel')}
        </label>
        <textarea
          id={`${reasonId}-field`}
          ref={field}
          data-composer-field
          className={styles.field}
          placeholder={t(
            replying
              ? 'v2.composer.replyPlaceholder'
              : 'v2.composer.placeholder',
          )}
          value={value}
          rows={1}
          disabled={!hasInquiry}
          aria-describedby={[shownBlock ? reasonId : null, helpId]
            .filter(Boolean)
            .join(' ')}
          onChange={event => onChange(event.target.value)}
          onCompositionEnd={() => {
            composedAt.current = Date.now();
          }}
          onFocus={() => {
            // The keyboard opens: keep the field in view.
            setTimeout(
              () => field.current?.scrollIntoView({ block: 'nearest' }),
              300,
            );
          }}
          onKeyDown={event => {
            const justComposed =
              Date.now() - composedAt.current < COMPOSITION_GRACE_MS;
            if (isSendKey(event, sendKey, justComposed)) {
              event.preventDefault();
              if (canSend) onSend({ readyOnly: false });
            }
          }}
          data-testid="composer-input"
        />
        {setup}
        <div className={styles.toolbar}>
          <div className={styles.tools}>
            <button
              type="button"
              className={styles.iconButton}
              aria-label={t('v2.composer.refCtx')}
              title={t('v2.composer.refCtx')}
              disabled={!hasInquiry}
              onClick={onOpenSources}
              data-testid="composer-sources"
            >
              <FolderAddOutlined aria-hidden />
            </button>
            <button
              type="button"
              className={compact ? styles.iconButton : styles.attachButton}
              aria-label={t('v2.composer.attachWithDrop')}
              title={t('v2.composer.attachWithDrop')}
              disabled={!hasInquiry}
              onClick={() => fileInput.current?.click()}
              data-testid="composer-attach"
            >
              <PaperClipOutlined aria-hidden />
              {compact ? null : <span>{t('v2.composer.attachDrop')}</span>}
            </button>
            <input
              ref={fileInput}
              type="file"
              multiple
              accept={accept}
              className={styles.srOnly}
              tabIndex={-1}
              aria-hidden="true"
              onChange={event => {
                if (event.target.files?.length) onFiles(event.target.files);
                event.target.value = '';
              }}
              data-testid="composer-file-input"
            />
            <ModePicker
              mode={mode}
              onChange={onModeChange}
              disabled={!hasInquiry}
              compact={compact}
            />
          </div>
          {generating ? (
            <button
              type="button"
              className={styles.stop}
              onClick={onStop}
              disabled={!canStop}
              data-testid="chat-stop"
            >
              <span className={styles.stopIcon} aria-hidden />
              {stopRequested
                ? t('v2.messages.st_stopping')
                : t('v2.common.stop')}
            </button>
          ) : (
            <button
              type="button"
              className={styles.send}
              onClick={() => onSend({ readyOnly: false })}
              disabled={!canSend}
              aria-label={t('v2.composer.send')}
              aria-describedby={shownBlock ? reasonId : undefined}
              title={t('v2.composer.send')}
              data-testid="chat-send"
            >
              {sending ? (
                <LoadingOutlined aria-hidden />
              ) : (
                <ArrowUpOutlined aria-hidden />
              )}
            </button>
          )}
        </div>
      </div>
      {shownBlock ? (
        <BlockingNotice
          block={shownBlock}
          id={reasonId}
          onAction={onBlockAction}
        />
      ) : null}
      <p id={helpId} className={styles.help} data-testid="composer-help">
        {t(help.key, help.values)}
      </p>
    </div>
  );
};

export default Composer;
